import { formatDistance } from 'date-fns'

import type { IUserActivity } from '@/models/IUserActivity'

export const USERS_ACTIVITY_MAX_IDS = 200

export const USER_ACTIVITY_HELP_TEXT =
  'Internal only. Based on sign-ins and session refreshes.'

export const USER_ACTIVITY_NO_DATA = '—'

export const USER_ACTIVITY_NO_DATA_HINT =
  'No data: not seen since activity tracking started.'

export const LAST_LOGIN_HINT = 'Last successful sign-in (any method).'

export const LAST_ACTIVITY_HINT =
  'Last time the user had 360 open in a visible tab (~10 min precision, from session refreshes).'

export const FIRST_SEEN_HINT =
  'First time the user was seen since activity tracking started.'

export type UserActivityMap = Record<string, IUserActivity>

export const getUserActivityKey = (orgId?: string | null, userId?: string) =>
  `${orgId ?? ''}:${userId ?? ''}`

export const chunk = <T>(items: T[], size: number): T[][] => {
  if (size <= 0) return [items]

  const chunks: T[][] = []

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }

  return chunks
}

/**
 * Builds the `user_ids` batches for the activity endpoint: de-duplicated, empty ids dropped,
 * max `USERS_ACTIVITY_MAX_IDS` per request.
 */
export const buildUserIdBatches = (
  userIds: (string | undefined | null)[],
  size = USERS_ACTIVITY_MAX_IDS
): string[][] => {
  const unique = Array.from(new Set(userIds.filter((id): id is string => !!id)))

  return unique.length ? chunk(unique, size) : []
}

export const toUserActivityMap = (results: IUserActivity[]): UserActivityMap =>
  results.reduce<UserActivityMap>((acc, activity) => {
    acc[getUserActivityKey(activity.org_id, activity.user_id)] = activity

    return acc
  }, {})

const parseDate = (value?: string | null): Date | undefined => {
  if (!value) return undefined

  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? undefined : date
}

export type FormattedActivityTime = {
  /** e.g. "3 days ago", or "—" when there is no data */
  relative: string
  /** absolute local timestamp, undefined when there is no data */
  absolute?: string
}

export const formatActivityTime = (
  value?: string | null,
  now: Date = new Date()
): FormattedActivityTime => {
  const date = parseDate(value)

  if (!date) return { relative: USER_ACTIVITY_NO_DATA }

  return {
    relative: formatDistance(date, now, { addSuffix: true }),
    absolute: date.toLocaleString()
  }
}

/** ascending comparator for nullable ISO timestamps; missing values sort last */
export const compareActivityTime = (
  a?: string | null,
  b?: string | null
): number => {
  const dateA = parseDate(a)
  const dateB = parseDate(b)

  if (!dateA && !dateB) return 0
  if (!dateA) return 1
  if (!dateB) return -1

  return dateA.getTime() - dateB.getTime()
}
